import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import MaskedReveal from "@/components/marketing/MaskedReveal";
import { mediaUrl } from "@/lib/api";

/**
 * Cinematic hero.
 * - Full-screen image or MP4 (autoplay, loop, muted, playsinline)
 * - Subtle parallax on the media
 * - Masked line-by-line title reveal
 * - Two CTAs
 * - Pauses video when tab inactive
 */
export default function Hero({ settings }) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end start"],
  });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.75]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onVis = () => {
      if (document.hidden) v.pause();
      else v.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const heroType = settings?.hero_type || "image";
  const heroImage = mediaUrl(settings?.hero_image_url || "");
  const heroVideo = mediaUrl(settings?.hero_video_url || "");
  const fallback = mediaUrl(settings?.hero_fallback_image || heroImage);
  const brand = settings?.brand_name || "OERESHAUS";
  const subtitle = settings?.brand_subtitle || "PHOTOGRAPHY & FILMS";
  const tagline = settings?.tagline || "Stories held in light.";
  const support =
    settings?.tagline_support ||
    "Cinematic photography for people, celebrations and stories worth remembering.";

  // Focal point (which part of the image is kept in view). Defaults to centre.
  const fx = settings?.hero_focal_x ?? 50;
  const fy = settings?.hero_focal_y ?? 50;
  const focalPosition = `${fx}% ${fy}%`;

  return (
    <section
      ref={wrapRef}
      data-testid="hero-section"
      className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-ink text-ivory"
    >
      <motion.div
        className="absolute inset-0"
        style={{
          y: mediaY,
          scale,
          willChange: "transform",
          backfaceVisibility: "hidden",
          transformStyle: "preserve-3d",
        }}
      >
        {heroType === "video" && heroVideo ? (
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            style={{ objectPosition: focalPosition }}
            autoPlay
            muted
            loop
            playsInline
            poster={fallback || undefined}
            data-testid="hero-video"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
        ) : heroImage || fallback ? (
          <img
            src={heroImage || fallback}
            alt="Oereshaus Photography Cape Town"
            className="w-full h-full object-cover"
            style={{ objectPosition: focalPosition }}
            data-testid="hero-image"
          />
        ) : (
          <div className="w-full h-full bg-ink" />
        )}
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity, willChange: "opacity" }}
        className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70 pointer-events-none"
      />

      <motion.div
        style={{ opacity: contentOpacity, willChange: "opacity" }}
        className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-14 md:pb-24"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-8 h-px bg-ivory/60" />
          <motion.span
            className="label-mono text-ivory/80"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.9 }}
            data-testid="hero-eyebrow"
          >
            {brand} · {subtitle}
          </motion.span>
        </div>

        <MaskedReveal
          as="h1"
          mode="load"
          delay={0.15}
          lines={tagline.split(" ").reduce((rows, word, i) => {
            // group into two natural lines
            const half = Math.ceil(tagline.split(" ").length / 2);
            if (i < half) rows[0] = (rows[0] || "") + (rows[0] ? " " : "") + word;
            else rows[1] = (rows[1] || "") + (rows[1] ? " " : "") + word;
            return rows;
          }, [])}
          className="font-editorial text-[13vw] md:text-[10vw] lg:text-[9vw] leading-[0.92] tracking-tight max-w-[95%]"
        />
        <span data-testid="hero-tagline" className="sr-only">{tagline}</span>

        <motion.p
          className="mt-8 max-w-xl text-base md:text-lg text-ivory/80 leading-relaxed"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.8 }}
          data-testid="hero-support"
        >
          {support}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link to="/portfolio" className="btn-solid" data-testid="hero-cta-portfolio">
            View Portfolio
          </Link>
          <Link to="/services" className="btn-outline-ivory" data-testid="hero-cta-services">
            Explore Services
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-6 right-6 md:bottom-10 md:right-12 label-mono text-ivory/60 flex items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
      >
        <span className="w-6 h-px bg-ivory/50" />
        SCROLL
      </motion.div>
    </section>
  );
}
